import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Slide,
  Switch,
  Toolbar,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { TransitionProps } from '@mui/material/transitions'
import { AgentTool, ToolCategory, availableTools } from '../model/tools'
import { MedconnectAgentConfig } from '@/core/models/account.model'
import { useChangeToolConfig } from '../model/useChangeToolConfig'

interface ToolConfigurationPanelProps {
  open: boolean
  onClose: () => void
  config: MedconnectAgentConfig
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export function ToolConfigurationPanel({ open, onClose, config }: ToolConfigurationPanelProps) {
  const { t } = useTranslation()
  const changeToolConfig = useChangeToolConfig()

  const handleToolToggle = (toolName: string, isEnabled: boolean) => {
    const { enabledTools = [], disabledTools = [] } = config

    let newEnabledTools = [...enabledTools]
    let newDisabledTools = [...disabledTools]

    if (isEnabled) {
      newEnabledTools = [...newEnabledTools, toolName]
      newDisabledTools = newDisabledTools.filter((t) => t !== toolName)
    } else {
      newEnabledTools = newEnabledTools.filter((t) => t !== toolName)
      newDisabledTools = [...newDisabledTools, toolName]
    }

    changeToolConfig({
      enabledTools: newEnabledTools,
      disabledTools: newDisabledTools,
    })
  }

  const handleActivateAll = () => {
    changeToolConfig({
      enabledTools: [],
      disabledTools: [],
    })
  }

  const handleDeactivateAll = () => {
    const allToolNames = availableTools.map((tool) => tool.name)
    changeToolConfig({
      enabledTools: [],
      disabledTools: allToolNames,
    })
  }

  const isToolEnabled = (toolName: string) => {
    const { enabledTools, disabledTools } = config

    if (disabledTools?.includes(toolName)) {
      return false
    }
    if (enabledTools?.length) {
      return enabledTools.includes(toolName)
    }
    return true
  }

  const toolsByCategory = availableTools.reduce((acc, tool) => {
    if (!acc[tool.category]) {
      acc[tool.category] = []
    }
    acc[tool.category].push(tool)
    return acc
  }, {} as Record<ToolCategory, AgentTool[]>)

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" color="white" component="div">
            {t('page.medconnectAgent.config.title')}
          </Typography>
          <Button color="primary" onClick={handleActivateAll}>
            {t('page.medconnectAgent.config.activateAll')}
          </Button>
          <Button color="primary" onClick={handleDeactivateAll}>
            {t('page.medconnectAgent.config.deactivateAll')}
          </Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('page.medconnectAgent.config.description')}
        </Typography>

        {Object.entries(toolsByCategory).map(([category, tools]) => (
          <Accordion key={category} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">{t(category as ToolCategory)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                {tools.map((tool) => (
                  <ListItem key={tool.name} sx={{ pl: 0, pr: 0 }}>
                    <ListItemText
                      primary={t(tool.titleKey)}
                      secondary={t(tool.descriptionKey)}
                      secondaryTypographyProps={{ style: { whiteSpace: 'pre-line' } }}
                    />
                    <Switch checked={isToolEnabled(tool.name)} onChange={(e) => handleToolToggle(tool.name, e.target.checked)} edge="end" />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Dialog>
  )
}
